<script lang="ts">
  import {
    getSectionGroupNavItemStyle,
    getSectionGroupNavStyle,
    getSectionGroupStyle,
    styleToString,
  } from "@shared/common/node/style";
  import type {
    IMediaPaint,
    ISectionGroupNode,
    PartialNode,
    SceneNode,
  } from "@shared/common/node/type";
  import { getAspectRatioStyle } from "@shared/common/node/util";
  import type { VideoInfo } from "@shared/common/types/data-source";
  import { getDataSourceValue } from "@shared/common/utils/data-source";
  import { pipe } from "@shared/common/utils/pipe";
  import { assertCondition, isObject } from "@shared/common/utils/value";
  import type { Properties } from "csstype";
  import { onMount } from "svelte";
  import detectReaction from "../../actions/detect-reaction";
  import { getCurrentDocumentNode, getStore } from "../../context";
  import getShadowRootOrDocument from "../../lib/get-shadow-root-or-document";
  import BackgroundVideo from "./background-video.svelte";
  import SectionGroupNavButton from "./section-group-nav-button.svelte";

  const { reactionEventListener, dataMap } = getStore();
  const documentNode = getCurrentDocumentNode();

  export let node: PartialNode<ISectionGroupNode>;
  // 캡처시 가장 최상단 프레임의 그림자를 제거
  export let hideShadow: boolean;

  let image: IMediaPaint | undefined = node.fills?.find(
    (fill): fill is IMediaPaint => fill.type === "IMAGE"
  );
  let isBackgroundVideo =
    node.fills && image?.visible && image?.video?.fileName;

  const videoInfoAssertion = assertCondition<VideoInfo[]>(
    (val): val is VideoInfo[] =>
      Array.isArray(val) &&
      val.every(
        (item) => isObject(item) && "status" in item && "videoUrl" in item
      )
  );

  $: videoSrc = videoInfoAssertion
    .typeSafeValue(
      getDataSourceValue({
        dataMap: $dataMap ?? {},
        keyString: "video",
      }),
      []
    )
    .find((video) => {
      if (image?.type === "IMAGE" && image.video?.fileName === video.filename) {
        return true;
      }
    });

  let navClientHeight = 0;
  let navClientWidth = 0;
  let holder: HTMLDivElement;
  let fixed = false;
  let navElement: HTMLUListElement;
  $: navChildren =
    node.children?.filter((child) => child.position?.type === "RELATIVE") || [];

  // distance between window and holder
  let boundingBoxLeft = 0;
  const { device } = getStore();

  onMount(() => {
    const navMarginTop =
      ($device === "DESKTOP" && node.desktopNavMarginTop
        ? node.desktopNavMarginTop
        : node.navMarginTop) ?? 0;

    const observer = new IntersectionObserver(
      () => {
        const rectTop = holder.getBoundingClientRect().top;
        fixed = rectTop < navMarginTop;
      },
      {
        threshold: 1,
        rootMargin: `-${navMarginTop}px 0px 0px 0px`,
      }
    );

    const calculateBoundingBoxLeft = () => {
      const rect = holder.parentElement?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      boundingBoxLeft = rect.left;
    };

    const resizeObserver = new ResizeObserver(calculateBoundingBoxLeft);
    calculateBoundingBoxLeft();

    observer.observe(holder);
    resizeObserver.observe(document.documentElement);

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  });

  $: holderStyle = getHolderStyle({
    height: navClientHeight,
  });

  function getHolderStyle({ height }: { height: number }) {
    const style: Properties = {
      height: `${height}px`,
    };

    return style;
  }

  function handleNavButtonActiveChange(
    e: CustomEvent<{ value: boolean; button: HTMLButtonElement }>
  ) {
    if (e.detail.value) {
      const button = e.detail.button;
      const scrollLeft =
        button.offsetLeft - navClientWidth / 2 + button.offsetWidth / 2;

      navElement.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }

  function handleNavButtonClick(child: PartialNode<SceneNode>) {
    const root = getShadowRootOrDocument(navElement);
    const element = root.querySelector(`#node-${child.id}`);
    if (!element) {
      // eslint-disable-next-line no-console
      console.warn(`element ${child.id}(${child.type}) not found`);
      return;
    }

    const offset = 100; // 원하는 offset 픽셀값
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }

  $: navStyle = getSectionGroupNavStyle({
    fixed,
    node,
    boundingBoxLeft,
    device: $device,
  });
</script>

<!-- 일반적인 프레임 -->
<div
  use:detectReaction={{
    node,
    documentNode,
    onAction: reactionEventListener,
  }}
  style={pipe(
    getSectionGroupStyle(node),
    (style) => {
      // background로 비디오가 있을 경우, 비디오의 해상도로 aspectRatio를 조정
      if (isBackgroundVideo) {
        if (node.aspectRatio && node.aspectRatio.type === "BACKGROUND_IMAGE") {
          style.aspectRatio = getAspectRatioStyle({
            type: node.aspectRatio.type,
            imageHeight: videoSrc?.height,
            imageWidth: videoSrc?.width,
          });
        }

        style.background = "none";
      }

      if (hideShadow) {
        const { boxShadow: _boxShadow, ...last } = style;
        return last;
      }

      return style;
    },
    styleToString
  )}
>
  {#if isBackgroundVideo}
    <BackgroundVideo fills={node.fills} {videoSrc} />
  {/if}
  <div
    class="holder"
    style={styleToString(holderStyle)}
    bind:this={holder}
  ></div>

  <ul
    class="nav"
    role="navigation"
    style={styleToString(navStyle)}
    bind:clientHeight={navClientHeight}
    bind:clientWidth={navClientWidth}
    bind:this={navElement}
  >
    {#each navChildren as child, index}
      <li
        style={styleToString(
          getSectionGroupNavItemStyle({
            index,
            length: navChildren.length,
            node,
          })
        )}
      >
        <SectionGroupNavButton
          node={child}
          on:activeChange={handleNavButtonActiveChange}
          on:click={() => {
            handleNavButtonClick(child);
          }}
        />
      </li>
    {/each}
  </ul>

  <slot />
</div>

<style>
  .nav {
    z-index: 99;
    left: 0;
    right: 0;
    display: flex;
    /* padding: 0px 20px; */
    overflow-x: auto;
    width: 100%;
    background-color: white;
    box-shadow: 0px 1px 0px rgba(0, 0, 0, 0.1);
    scrollbar-width: none; /* Firefox */
  }

  .nav::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
</style>
